// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import PropTypes from "prop-types";

import { Header } from "components/elements";

import styles from "./ContentBox.scss";

/**
 * @typedef {Object} ContentBoxProps
 * @property {string|React.ReactNode} [title] - The title to display in the content box header
 * @property {React.ReactNode} [text] - Text content to display in the box
 * @property {React.ReactNode} [children] - Child components to render in the box
 * @property {boolean} [uppercaseTitle=true] - Whether to display the title in uppercase
 */

/**
 * ContentBox component that displays information in a styled aside container with a header
 * @param {ContentBoxProps} props - The component props
 * @returns {JSX.Element} The rendered content box component
 */
export function ContentBox({ text, title, children, uppercaseTitle = true }) {
  return (
    <aside className={styles.contentBox} data-test-id="content-box">
      <div className={styles.additionalInformation}>
        <Header
          uppercase={uppercaseTitle}
          bold={true}
          color="secondary"
          className={styles.informationHeader}
        >
          {title}
        </Header>
        {text || children}
      </div>
    </aside>
  );
}

ContentBox.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  text: PropTypes.node,
  children: PropTypes.node,
  uppercaseTitle: PropTypes.bool,
};
